import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Field, FieldArray, Form, Formik, FormikErrors, FormikTouched } from 'formik';
import { ApplicationContext } from '../../context';
import { Environment } from 'src/Environment';
import { MutableList, MutableListItem } from 'src/components/mutable-list/mutable-list';
import { DirectorInfoFields } from './director-info-fields/director-info-fields';
import { asylumRegex, passportRegex, refugeeRegex, stripWhitespace } from 'src/helpers/formatting';
import { isValidID } from 'src/helpers/validation';
import {
  isUserUnderAgeIdNumber,
  isUnderAge as isUserUnderAge,
  isValidPassportExpiryDate,
} from 'src/features/common';
import {
  Director,
  DirectorDetails,
  IdentityTypeDirectors,
} from 'src/features/common/types/application/director-details';
import {
  ApplicationActionType,
  FormLayout,
  PageLayout,
  useWizardEffect,
  HeadingLayout,
  HeadingOne,
  HeadingTwo,
  ScrollTop,
  HelpButton,
  SolutionType,
  getWordCase,
} from 'src/features/common';
import {
  MutableAccordion,
  MutableAccordionItem,
} from 'src/features/common/components/mutable-accordion/mutable-accordion';
import { FormikChangeListener } from 'src/features/order/components/product-quantity-change-listener/product-quantity-change-listener';
import { useAsyncDispatch } from '../../../order/hooks/use-async-dispatch';
import { getApplicationAPIFromApplication } from '../../../common/helpers/dto-mappers/salesforce/get-application-api-from-application';
import { getBusinessTypeDirectorLookup } from './resources/business-type-to-director-lookup';
import { PAGE_NAME } from 'src/features/common/types/tealium-service';
import { UnderAgeModal } from 'src/features/common/components/underage-modal/underage-modal';
import { killApplication } from 'src/features/common/reducers/application/application-reducer-helpers';
import { getDirectorDetailsHeadings } from 'src/features/onboarding/helpers/headings';
import { FormStatus, getDirectorsFormStatus } from '../../helpers/fields-status';
import OnboardingTealiumService from '../../services/onboarding-tealium-services';
import './director-details-page.scss';

export type IdentityTypeItem = {
  value: IdentityTypeDirectors;
  label: string;
};

export const DirectorIdTypes: IdentityTypeItem[] = [
  { value: IdentityTypeDirectors.RSA_ID, label: 'SA ID' },
  { value: IdentityTypeDirectors.PASSPORT, label: 'Passport' },
  { value: IdentityTypeDirectors.REFUGEE, label: 'Refugee' },
  { value: IdentityTypeDirectors.ASYLUM_SEEKER, label: 'Asylum seeker' },
];

interface IFormDirector {
  idType?: IdentityTypeItem;
  title?: string;
  initials?: string;
  firstName?: string;
  surname?: string;
  idNumber?: string;
  passportNumber?: string;
  dateOfBirth?: Date;
  gender?: string;
  passportExpiryDate?: Date;
  countryOfOrigin?: string;
}

interface FormType {
  directors: IFormDirector[];
}

export const DirectorDetailsPage = () => {
  const { application, dispatch, salesforceService, catalogue } = useContext(ApplicationContext);

  const dispatchAsync = useAsyncDispatch(application, dispatch);
  const businessType = application.businessDetails?.companyType;
  const creditVettingEnabled: boolean = !!application.creditVetting?.required;
  let formTouchedCount = 0;

  const creditVettingRequired = useMemo((): boolean => !!application.creditVetting?.required, []);

  const isForeignNationalsEnabled =
    !Environment.current?.disabledFeatures?.passport && !creditVettingRequired;

  const isThirdPartyJourney = useMemo(
    () => application.solutionType === SolutionType.thirdPartyOnboarding,
    [application.solutionType],
  );

  const headings = useMemo(
    () =>
      getDirectorDetailsHeadings(
        isThirdPartyJourney,
        getBusinessTypeDirectorLookup(true, businessType) || '',
      ),
    [isThirdPartyJourney],
  );

  const taggingJourney = useMemo(() => {
    switch (true) {
      case !!application.pendingApp?.isPending:
        return 'pending app';

      case !!application.brokerLogin || application.agentId:
        return 'third party';

      case application.deviceActivationTagging:
        return 'register device';

      default:
        return 'onboarding';
    }
  }, []);

  const isFormValid = (): boolean => Object.keys(validate(initialValues())).length === 0;
  const [isUnderAge, setUnderAge] = useState(false);

  const { setNextButtonEnabled, setOnNextEventHandler, setLoadingOverlayVisible } = useWizardEffect(
    () => ({
      nextButtonLabel: 'Next',
      nextButtonEnabled: isFormValid(),
    }),
    [application],
  );

  useEffect(() => {
    application.tapOnPhoneJourney
      ? OnboardingTealiumService.onDirectorsDetailsToPStart()
      : OnboardingTealiumService.onDirectorsDetailsStart(taggingJourney);
  }, []);

  const initialValues = (): FormType => {
    const director: IFormDirector[] = (application.directors?.directors || []).map((director) => ({
      title: director.title,
      initials: director.initials,
      firstName: director.firstName,
      surname: director.surname,
      idNumber: director.idNumber,
      passportNumber: director.passportNumber,
      dateOfBirth: director.dateOfBirth,
      gender: director.gender,
      passportExpiryDate: director.passportExpiryDate,
      countryOfOrigin: director.countryOfOrigin,
      idType: DirectorIdTypes.find((type) => type.value === director.idType),
    }));

    return {
      directors:
        director.length > 0
          ? director
          : [
              {
                idType: DirectorIdTypes.find((type) => type.value === IdentityTypeDirectors.RSA_ID),
              },
            ],
    };
  };

  const initialStatus = useMemo((): FormStatus | undefined => {
    if (!application.pendingApp?.isPending) {
      return undefined;
    }

    if (!application.pendingApp.pageUpdates) {
      throw new Error('Director details revamp: isPending set but no page updates available');
    }

    const allFields = initialValues();
    return getDirectorsFormStatus(application.pendingApp.pageUpdates, allFields);
  }, [application.pendingApp]);

  const onAddDirector = (_, addDirector) => {
    addDirector({
      idType: DirectorIdTypes.find((type) => type.value === IdentityTypeDirectors.RSA_ID),
    });
  };

  const validate = ({ directors }: FormType): FormikErrors<FormType> => {
    const errors: FormikErrors<FormType> = {
      directors: [],
    };
    let isInit = false;

    const initCheck = () => {
      if (!isInit) {
        errors.directors = [];
        isInit = true;
      }
    };

    const creditVettingCheck = (director: IFormDirector): FormikErrors<IFormDirector> => {
      const directorErrors: FormikErrors<IFormDirector> = {};

      if (!director.title) {
        directorErrors.title = 'Please select a title.';
      }

      if (!director.initials) {
        directorErrors.initials = 'Please enter your initials.';
      }

      if (!director.firstName) {
        directorErrors.firstName = 'Please enter your first name.';
      }

      if (!director.surname) {
        directorErrors.surname = 'Please enter your surname.';
      }

      if (director.idType?.value === IdentityTypeDirectors.RSA_ID) {
        if (!director.idNumber || !isValidID(stripWhitespace(director.idNumber))) {
          directorErrors.idNumber = 'ID number is invalid';
        }

        if (
          director.idNumber &&
          isValidID(stripWhitespace(director.idNumber)) &&
          isUserUnderAgeIdNumber(director.idNumber)
        ) {
          director.idNumber = 'The age limit is 18 years or older';
          setUnderAge(true);
          if (isUnderAge) {
            OnboardingTealiumService.onUnderageModalStart(taggingJourney);
          }
        }
      } else {
        // Passport
        if (!director.passportNumber) {
          directorErrors.passportNumber = 'Passport number is invalid.';
        }

        if (!director.dateOfBirth) {
          directorErrors.dateOfBirth = 'Date of birth is invalid.';
        }

        if (director.dateOfBirth && isUserUnderAge(new Date(director.dateOfBirth))) {
          directorErrors.dateOfBirth = 'The age limit is 18 years or older';
          setUnderAge(true);
        }

        if (!director.gender) {
          directorErrors.gender = 'Gender is invalid.';
        }

        if (!director.passportExpiryDate) {
          directorErrors.passportExpiryDate = 'Passport expiry date is invalid.';
        }

        if (!director.countryOfOrigin) {
          directorErrors.countryOfOrigin = 'Country of origin is invalid.';
        }
      }

      return directorErrors;
    };

    // TODO: once foreign nationals enabled on all env's remove below condition and two else if blocks beneath.
    // These blocks are still here to cater for Trunk based dev behind a flag.

    if (isForeignNationalsEnabled) {
      const seenIdNumbers: { [key: string]: boolean } = {
        [`${application.contact?.idNumber}`]: true,
      };

      directors.forEach((director, index) => {
        const directorErrors: FormikErrors<Director> = {};

        if (director.idType?.value === IdentityTypeDirectors.RSA_ID) {
          if (
            director.idNumber &&
            isValidID(stripWhitespace(director.idNumber)) &&
            isUserUnderAgeIdNumber(director.idNumber)
          ) {
            directorErrors.idNumber = 'The age limit is 18 years or older';
            setUnderAge(true);
            if (isUnderAge) {
              OnboardingTealiumService.onUnderageModalStart(taggingJourney);
            }
          }

          if (!director.idNumber || !isValidID(stripWhitespace(director.idNumber))) {
            directorErrors.idNumber = 'ID number is invalid';
          }

          if (director.idNumber && seenIdNumbers[director.idNumber]) {
            if (Object.keys(seenIdNumbers).length === 1) {
              directorErrors.idNumber =
                "ID identical to applicant's ID. Please enter a different ID number.";
            } else {
              directorErrors.idNumber =
                'Duplicate ID number provided. Please provide a unique ID number';
            }
          } else if (director.idNumber) {
            seenIdNumbers[director.idNumber] = true;
          }
        }

        if (
          director.idType?.value === IdentityTypeDirectors.PASSPORT &&
          (!director.idNumber || !passportRegex.test(director.idNumber))
        ) {
          directorErrors.idNumber = 'The Passport number entered is invalid';
        }

        if (
          director.idType?.value === IdentityTypeDirectors.REFUGEE &&
          (!director.idNumber || !refugeeRegex.test(director.idNumber))
        ) {
          directorErrors.idNumber = 'The Document ID entered is invalid';
        }

        if (
          director.idType?.value === IdentityTypeDirectors.ASYLUM_SEEKER &&
          (!director.idNumber || !asylumRegex.test(director.idNumber))
        ) {
          directorErrors.idNumber = 'The Document ID entered is invalid';
        }

        if (
          director.idType?.value === IdentityTypeDirectors.REFUGEE ||
          director.idType?.value === IdentityTypeDirectors.ASYLUM_SEEKER
        ) {
          if (!director.dateOfBirth) {
            directorErrors.dateOfBirth = 'Date of birth is invalid';
          }

          if (director.dateOfBirth && isUserUnderAge(new Date(director.dateOfBirth))) {
            directorErrors.dateOfBirth = 'The age limit is 18 years or older';
            setUnderAge(true);
          }
        }

        if (director.idType?.value !== IdentityTypeDirectors.RSA_ID) {
          if (!director.passportExpiryDate) {
            directorErrors.passportExpiryDate = `${
              director.idType?.value === IdentityTypeDirectors.PASSPORT ? 'Passport' : 'Document'
            } expiry date is invalid`;
          }

          if (!director.countryOfOrigin) {
            directorErrors.countryOfOrigin = 'Country of origin is invalid';
          }

          if (
            director.passportExpiryDate &&
            !isValidPassportExpiryDate(director.passportExpiryDate)
          ) {
            directorErrors.passportExpiryDate = `Your ${
              director.idType?.value === IdentityTypeDirectors.PASSPORT ? 'passport' : 'document'
            } must be valid for a minimum of 6 months.`;
          }

          if (!director.dateOfBirth) {
            directorErrors.dateOfBirth = 'Date of birth is invalid';
          }

          if (director.dateOfBirth && isUserUnderAge(new Date(director.dateOfBirth))) {
            directorErrors.dateOfBirth = 'The age limit is 18 years or older';
            setUnderAge(true);
          }
        }

        if (director.idType?.value !== IdentityTypeDirectors.RSA_ID || creditVettingEnabled) {
          if (!director.firstName) {
            directorErrors.firstName = 'Please enter your first name.';
          }

          if (!director.surname) {
            directorErrors.surname = 'Please enter your surname.';
          }
        }

        if (creditVettingEnabled) {
          if (!director.title) {
            directorErrors.title = 'Please select a title.';
          }

          if (!director.initials) {
            directorErrors.initials = 'Please enter your initials.';
          }

          if (director.idType?.value !== IdentityTypeDirectors.RSA_ID && !director.gender) {
            directorErrors.gender = 'Gender is invalid.';
          }
        }

        if (Object.keys(directorErrors).length > 0) {
          // @ts-ignore
          errors.directors[index] = directorErrors;
        }
      });
    } else if (creditVettingEnabled) {
      directors.forEach((director, index) => {
        const directorErrors: FormikErrors<Director> = creditVettingCheck(director);

        if (Object.keys(directorErrors).length > 0) {
          initCheck();
          // @ts-ignore
          errors.directors[index] = directorErrors;
        }
      });
    } else {
      const seenIdNumbers: { [key: string]: boolean } = {};
      directors.forEach(({ idNumber }, index) => {
        const directorErrors: FormikErrors<Director> = {};

        if (idNumber && isValidID(stripWhitespace(idNumber)) && isUserUnderAgeIdNumber(idNumber)) {
          directorErrors.idNumber = 'The age limit is 18 years or older';
          setUnderAge(true);
          if (isUnderAge) {
            OnboardingTealiumService.onUnderageModalStart(taggingJourney);
          }
        }

        if (!idNumber || !isValidID(stripWhitespace(idNumber))) {
          directorErrors.idNumber = 'ID number is invalid';
        }

        if (idNumber && seenIdNumbers[idNumber]) {
          directorErrors.idNumber =
            'Duplicate ID number provided. Please provide a unique ID number';
        } else if (idNumber) {
          seenIdNumbers[idNumber] = true;
        }

        if (Object.keys(directorErrors).length > 0) {
          initCheck();
          // @ts-ignore
          errors.directors[index] = directorErrors;
        }
      });
    }

    const isErrors = errors?.directors?.length !== 0;
    setNextButtonEnabled(!isErrors);

    if (isErrors) {
      return errors;
    } else {
      return {};
    }
  };

  const submit = async (values: FormType) => {
    const directors: DirectorDetails = {
      directors: values.directors.map((director) => ({
        idType: director.idType?.value,
        title: director.title,
        initials: director.initials,
        firstName: director.firstName,
        surname: director.surname,
        idNumber: director.idNumber,
        passportNumber: director.passportNumber,
        dateOfBirth: director.dateOfBirth,
        gender: director.gender,
        passportExpiryDate: director.passportExpiryDate,
        countryOfOrigin: director.countryOfOrigin,
      })),
    };

    setLoadingOverlayVisible(true);
    const applicationNew = await dispatchAsync({
      type: ApplicationActionType.UPDATE_DIRECTOR_DETAILS,
      directorDetails: directors,
    });
    await salesforceService.saveApplication(
      getApplicationAPIFromApplication(applicationNew, catalogue),
    );

    application.tapOnPhoneJourney
      ? OnboardingTealiumService.onDirectorsDetailsToPEnd()
      : OnboardingTealiumService.onDirectorsDetailsEnd(
          values.directors
            .map<string>((director) => director.idType?.value || '')
            .filter((idType) => idType !== ''),
          taggingJourney,
        );

    dispatch({ type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP });
    setLoadingOverlayVisible(false);
  };

  const sendFormStartEvent = (touched: FormikTouched<FormType>) => {
    if (Object.values(touched).some(Boolean) && formTouchedCount == 0) {
      OnboardingTealiumService.onDirectorsDetailsToPFormStart();
      formTouchedCount++;
    }
  };

  return (
    <article className="director-details-page">
      <HeadingLayout
        renderMainHeading={() => <HeadingOne>{headings.main}</HeadingOne>}
        renderSubHeading={() => (
          <HeadingTwo>
            {headings.sub}, we
            <br />
            will keep it safe.
          </HeadingTwo>
        )}
      />
      <PageLayout>
        <ScrollTop />
        <FormLayout>
          <UnderAgeModal
            visible={isUnderAge}
            onClose={() => {
              OnboardingTealiumService.onUnderageModalEnd(taggingJourney);
              setUnderAge(false);
            }}
            onExitApplication={async () => {
              OnboardingTealiumService.onUnderageModalExitAction(taggingJourney);
              const applicationNew = await dispatchAsync({
                type: ApplicationActionType.KILL_APPLICATION,
              });
              await salesforceService.saveApplication(killApplication(applicationNew, catalogue));
            }}
          />

          <Formik<FormType>
            innerRef={(formik) => formik && setOnNextEventHandler(formik.submitForm)}
            initialValues={initialValues()}
            initialStatus={initialStatus}
            validate={validate}
            onSubmit={submit}
            validateOnBlur
          >
            {({ values, setFieldValue, touched }) => (
              <Form>
                {sendFormStartEvent(touched)}
                <FormikChangeListener<FormType>>
                  {(oldValues, newValues) => {
                    oldValues.directors.forEach((oldDirector, index) => {
                      const correspondingNewDirector = newValues.directors.at(index);

                      if (
                        correspondingNewDirector &&
                        oldDirector.idType !== correspondingNewDirector.idType
                      ) {
                        setFieldValue(`directors[${index}].idNumber`, '');
                      }
                    });
                  }}
                </FormikChangeListener>

                {/* @ts-ignore */}
                <FieldArray name="directors">
                  {({ push: addDirector, remove: removeDirectorAtIndex }) => {
                    const mutableListItems: MutableListItem[] = values.directors.map(
                      (director, index) => {
                        return {
                          remove: () => {
                            removeDirectorAtIndex(index);
                          },
                          render: () => (
                            <Field
                              name={`directors[${index}]`}
                              component={DirectorInfoFields}
                              automationIdSuffix={index}
                              idInputFieldPlaceHolderText={getBusinessTypeDirectorLookup(
                                false,
                                businessType,
                              )}
                              passportInputFieldPlaceHolderText="Passport number"
                              isCreditVettingEnabled={creditVettingEnabled}
                              isForeignNationalsEnabled={isForeignNationalsEnabled}
                              automationIdPrefix={`Revamp_Director_Details_Director_${index + 1}`}
                            />
                          ),
                          automationIdDescription: 'your business ids',
                        };
                      },
                    );

                    const getMutableAccordionItems = (): MutableAccordionItem[] =>
                      mutableListItems.map((listItem, index) => {
                        const director = values.directors[index];
                        const isDirectorDisabled = Object.values(
                          initialStatus?.initialDisabled.directors[index] || {},
                        ).every((disabled) => disabled);

                        return {
                          ...listItem,
                          disabled: application.pendingApp?.isPending && isDirectorDisabled,
                          title:
                            director.title && director.firstName && director.surname
                              ? `${director.title} ${director.firstName} ${director.surname}`
                              : `${index + 1}. ${getWordCase(
                                  getBusinessTypeDirectorLookup(true, businessType),
                                )} details`,
                        } as MutableAccordionItem;
                      });

                    const automationIdPrefix = 'Revamp_Director_Details';

                    return (
                      <>
                        {creditVettingEnabled || isForeignNationalsEnabled ? (
                          <MutableAccordion
                            items={getMutableAccordionItems()}
                            automationIdPrefix={automationIdPrefix}
                          />
                        ) : (
                          <MutableList
                            items={mutableListItems}
                            automationIdPrefix={automationIdPrefix}
                          />
                        )}

                        {!application.pendingApp?.isPending && (
                          <label className="add-id-button">
                            <button
                              className={`is-hidden`}
                              onClick={() => {
                                OnboardingTealiumService.onDirectorsDetailsAction(taggingJourney);
                                onAddDirector(values.directors, addDirector);
                              }}
                            />
                            <span
                              automationid={`${automationIdPrefix}_Add_Button`}
                              className={`is-icon-4 icon-plus reskin-button`}
                            />
                            <span className="tiny-sub-heading">
                              Add a {getBusinessTypeDirectorLookup(true, businessType)}
                            </span>
                          </label>
                        )}
                      </>
                    );
                  }}
                </FieldArray>
              </Form>
            )}
          </Formik>
        </FormLayout>
      </PageLayout>
      <HelpButton
        onOpen={() =>
          OnboardingTealiumService.onHelpButtonActions(
            PAGE_NAME.DIRECTOR_DETAILS,
            'help',
            taggingJourney,
          )
        }
        onEmailClicked={() =>
          OnboardingTealiumService.onHelpButtonActions(
            PAGE_NAME.DIRECTOR_DETAILS,
            'email support',
            taggingJourney,
          )
        }
        onPhoneNumberClicked={() =>
          OnboardingTealiumService.onHelpButtonActions(
            PAGE_NAME.DIRECTOR_DETAILS,
            'call support number',
            taggingJourney,
          )
        }
      />
    </article>
  );
};
